import React, { useMemo, useState, useRef, useEffect } from "react";
import axios from "axios";
import { logout } from "../services/authService";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { useNavigate } from "react-router-dom";
import {
  Camera, User, Shield, Mail, Phone, Calendar,
  CheckCircle2, Save, Edit3, MapPin, Briefcase,
  ChevronRight, LogOut, Key, ShieldCheck, AlertCircle,
  Globe, Building2,
} from "lucide-react";

const AVATAR_GRADIENT = "linear-gradient(135deg, var(--primaryColor) 0%, var(--primaryLight) 100%)";

const NAV = [
  { key: "profile",  icon: User,   label: "Profile",  sub: "Personal details" },
  { key: "security", icon: Shield, label: "Security", sub: "Password & 2FA" },
];

const ProfileScreen = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab]               = useState("profile");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [saved, setSaved]                       = useState(false);
  const [editMode, setEditMode]                 = useState(false);
  const fileInputRef                            = useRef(null);

  const [profile, setProfile] = useState({
    firstName: "", lastName: "", email: "", phone: "", role: "", avatar: "",
  });
  const [pendingImage, setPendingImage] = useState(null); // base64 data URI staged for upload
  const avatarObjectUrlRef = useRef(null);

  const loadProfilePicture = React.useCallback((profileImageUrl) => {
    if (avatarObjectUrlRef.current) {
      URL.revokeObjectURL(avatarObjectUrlRef.current);
      avatarObjectUrlRef.current = null;
    }
    if (!profileImageUrl) {
      setProfile(p => ({ ...p, avatar: "" }));
      return;
    }
    const token = localStorage.getItem("token");
    fetch(`${process.env.REACT_APP_BASE_URL}${profileImageUrl}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) throw new Error("no picture");
      return res.blob();
    }).then(blob => {
      const objectUrl = URL.createObjectURL(blob);
      avatarObjectUrlRef.current = objectUrl;
      setProfile(p => ({ ...p, avatar: objectUrl }));
    }).catch(() => {});
  }, []);

  const [sessions, setSessions]     = useState([]);
  const [sessTotal, setSessTotal]   = useState(0);
  const [sessLoading, setSessLoading] = useState(false);
  const [sessHasMore, setSessHasMore] = useState(false);
  const sessBottomRef    = useRef(null);
  const sessPageRef      = useRef(1);
  const sessLoadingRef   = useRef(false);
  const sessHasMoreRef   = useRef(false);
  const sessInitialized  = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    axios.get(`${process.env.REACT_APP_BASE_URL}/api/v1/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const u = res.data?.response;
      if (u) {
        setProfile({
          firstName: u.firstName || "",
          lastName:  u.lastName  || "",
          email:     u.email     || "",
          phone:     u.phone     || "",
          role:      u.role      || "",
          avatar:    "",
        });
        loadProfilePicture(u.profileImageUrl);
      }
    }).catch(() => {});
  }, [loadProfilePicture]);

  useEffect(() => () => {
    if (avatarObjectUrlRef.current) URL.revokeObjectURL(avatarObjectUrlRef.current);
  }, []);

  const loadSessions = React.useCallback((pg, append = false) => {
    if (sessLoadingRef.current) return;
    const token = localStorage.getItem("token");
    sessLoadingRef.current = true;
    setSessLoading(true);
    axios.post(`${process.env.REACT_APP_BASE_URL}/api/v1/auth/sessions`, { page: pg, limit: 10 }, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      const items = res.data?.response?.items ?? [];
      const pagination = res.data?.response?.pagination ?? null;
      setSessions(prev => append ? [...prev, ...items] : items);
      const hasNext = pagination?.hasNextPage ?? false;
      setSessHasMore(hasNext);
      sessHasMoreRef.current = hasNext;
      setSessTotal(pagination?.total ?? 0);
    }).catch(() => {})
    .finally(() => { setSessLoading(false); sessLoadingRef.current = false; });
  }, []);

  // Load first page when security tab is opened for the first time
  useEffect(() => {
    if (activeTab !== "security") return;
    if (sessInitialized.current) return;
    sessInitialized.current = true;
    sessPageRef.current = 1;
    loadSessions(1, false);
  }, [activeTab, loadSessions]);

  // IntersectionObserver — re-attach whenever security tab becomes active
  useEffect(() => {
    if (activeTab !== "security") return;
    // sentinel may not exist in DOM yet; wait one tick
    const timer = setTimeout(() => {
      const sentinel = sessBottomRef.current;
      if (!sentinel) return;
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && sessHasMoreRef.current && !sessLoadingRef.current) {
          const next = sessPageRef.current + 1;
          sessPageRef.current = next;
          loadSessions(next, true);
        }
      }, { threshold: 0.1 });
      observer.observe(sentinel);
      // store on ref so cleanup can disconnect
      sessBottomRef._observer = observer;
    }, 0);
    return () => {
      clearTimeout(timer);
      sessBottomRef._observer?.disconnect();
    };
  }, [activeTab, loadSessions]);

  const initials = useMemo(
    () => `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`.toUpperCase(),
    [profile],
  );

  const set = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPendingImage(reader.result);
      set("avatar", reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    const token = localStorage.getItem("token");
    try {
      const res = await axios.patch(`${process.env.REACT_APP_BASE_URL}/api/v1/auth/profile`, {
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        ...(pendingImage ? { profileImage: pendingImage } : {}),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setEditMode(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      if (pendingImage) {
        setPendingImage(null);
        const profileImageUrl = res.data?.response?.profileImageUrl;
        loadProfilePicture(profileImageUrl);
        window.dispatchEvent(new CustomEvent("profile-image-updated", { detail: { profileImageUrl } }));
      }
    } catch (err) {
      const errors = err?.response?.data?.response?.errors;
      alert(errors ? errors.join("\n") : "Failed to update profile.");
    }
  };

  return (
    <main className="app-wrapper prf3-page">
      <div className="prf3-wrap">


        {/* ── Top Bar ── */}
        <div className="prf3-topbar">
          <div className="prf3-topbar-user">
            <div
              className="prf3-tb-avatar-wrap"
              onClick={() => editMode && activeTab === "profile" && fileInputRef.current?.click()}
              style={{ cursor: editMode && activeTab === "profile" ? "pointer" : "default" }}
            >
              <div className="prf3-tb-avatar" style={{ background: profile.avatar ? "transparent" : AVATAR_GRADIENT }}>
                {profile.avatar
                  ? <img src={profile.avatar} alt="avatar" className="prf3-avatar-img" />
                  : <span className="prf3-tb-initials">{initials}</span>
                }
                {editMode && activeTab === "profile" && (
                  <div className="prf3-tb-avatar-overlay"><Camera size={13} /></div>
                )}
              </div>
              <span className="prf3-status-dot" />
              {editMode && activeTab === "profile" && (
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatar} />
              )}
            </div>
            <div>
              <p className="prf3-tb-name">{profile.firstName} {profile.lastName}</p>
              <p className="prf3-tb-role">{profile.role}</p>
            </div>
          </div>
          <div className="prf3-topbar-nav">
            {NAV.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                className={`prf3-tb-btn${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <Icon size={14} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Main Content ── */}
        <div className="prf3-main">

          {/* ════ PROFILE TAB ════ */}
          {activeTab === "profile" && (
            <div className="prf3-panel">

              {/* Panel header */}
              <div className="prf3-panel-head">
                <div className="prf3-panel-head-left">
                  <div className="prf3-panel-ico prf3-panel-ico-blue">
                    <User size={18} />
                  </div>
                  <div>
                    <h2 className="prf3-panel-title">Personal Information</h2>
                    <p className="prf3-panel-sub">Update your photo and personal details here</p>
                  </div>
                </div>
                {!editMode ? (
                  <button className="prf3-btn prf3-btn-outline" onClick={() => setEditMode(true)}>
                    <Edit3 size={14} /> Edit Profile
                  </button>
                ) : (
                  <div className="prf3-btn-group">
                    <button
                      className="prf3-btn prf3-btn-ghost"
                      onClick={() => {
                        setEditMode(false);
                        if (pendingImage) {
                          setPendingImage(null);
                          set("avatar", avatarObjectUrlRef.current || "");
                        }
                      }}
                    >
                      Cancel
                    </button>
                    <button className={`prf3-btn prf3-btn-primary${saved ? " saved" : ""}`} onClick={handleSave}>
                      {saved ? <><CheckCircle2 size={14} /> Saved!</> : <><Save size={14} /> Save Changes</>}
                    </button>
                  </div>
                )}
              </div>

              <div className="prf3-panel-body">

                {/* View mode */}
                {!editMode ? (
                  <>
                    <div className="prf3-view-grid">
                      {[
                        { icon: User,  label: "First Name", val: profile.firstName },
                        { icon: User,  label: "Last Name",  val: profile.lastName },
                        { icon: Mail,  label: "Email",      val: profile.email },
                        { icon: Phone, label: "Phone",      val: profile.phone },
                      ].map(({ icon: Icon, label, val }) => (
                        <div key={label} className="prf3-view-item">
                          <div className="prf3-view-ico"><Icon size={13} /></div>
                          <div className="prf3-view-content">
                            <span className="prf3-view-lbl">{label}</span>
                            <span className="prf3-view-val">{val || "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  /* Edit mode */
                  <div className="prf3-form-grid">
                    <div className="prf3-field">
                      <label className="prf3-label">First Name</label>
                      <input className="prf3-input" value={profile.firstName} onChange={e => set("firstName", e.target.value)} placeholder="First name" />
                    </div>
                    <div className="prf3-field">
                      <label className="prf3-label">Last Name</label>
                      <input className="prf3-input" value={profile.lastName} onChange={e => set("lastName", e.target.value)} placeholder="Last name" />
                    </div>
                    <div className="prf3-field prf3-field-full">
                      <label className="prf3-label">Email Address</label>
                      <div className="prf3-input-wrap">
                        <Mail size={15} className="prf3-input-ico" />
                        <input className="prf3-input prf3-input-pl" value={profile.email} readOnly style={{ background: "#f8fafc", color: "#94a3b8", cursor: "not-allowed" }} type="email" />
                      </div>
                    </div>
                    <div className="prf3-field prf3-field-full">
                      <label className="prf3-label">Phone Number</label>
                      <div className="prf3-input-wrap">
                        <Phone size={15} className="prf3-input-ico" />
                        <input className="prf3-input prf3-input-pl" value={profile.phone} onChange={e => set("phone", e.target.value)} type="tel" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════ SECURITY TAB ════ */}
          {activeTab === "security" && (
            <div className="prf3-panel">
              <div className="prf3-panel-head">
                <div className="prf3-panel-head-left">
                  <div className="prf3-panel-ico prf3-panel-ico-green">
                    <Shield size={18} />
                  </div>
                  <div>
                    <h2 className="prf3-panel-title">Security Settings</h2>
                    <p className="prf3-panel-sub">Manage your password and account access</p>
                  </div>
                </div>
              </div>

              <div className="prf3-panel-body prf3-sec-body">

                {/* Password */}
                <div className="prf3-sec-item">
                  <div className="prf3-sec-item-ico prf3-sec-ico-blue"><Key size={19} /></div>
                  <div className="prf3-sec-item-info">
                    <p className="prf3-sec-item-title">Password</p>
                    <p className="prf3-sec-item-sub">Last changed 3 months ago · ••••••••••</p>
                  </div>
                  <button className="prf3-btn prf3-btn-outline" onClick={() => setShowPasswordModal(true)}>
                    <Edit3 size={13} /> Change
                  </button>
                </div>


                {/* Sessions */}
                <div className="prf3-sessions-block">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <p className="prf3-sessions-title" style={{ margin: 0 }}>Active Sessions</p>
                    {sessTotal > 0 && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{sessTotal} total</span>
                    )}
                  </div>
                  <div className="prf3-sessions-list">
                    {sessions.length === 0 && sessLoading ? (
                      <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                        <div className="sess-spinner" />
                      </div>
                    ) : sessions.length === 0 ? (
                      <p style={{ fontSize: 13, color: "#94a3b8", padding: "8px 0" }}>No sessions found.</p>
                    ) : sessions.map((s, i) => {
                      const isCurrent = i === 0;
                      const deviceLabel = `${s.device?.browserName || "Browser"} on ${s.device?.osProvider || s.device?.deviceType || "Device"}`;
                      const isMobile = s.device?.deviceType === "mobile";
                      const lastActive = s.lastActiveAt
                        ? new Date(s.lastActiveAt * 1000).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
                        : s.createdAt ? new Date(s.createdAt).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
                      return (
                        <div key={s.sessId} className="prf3-session-row">
                          <div className={`prf3-session-ico ${isMobile ? "prf3-session-ico-m" : ""}`}>
                            <Monitor size={16} />
                          </div>
                          <div className="prf3-session-info">
                            <p className="prf3-session-device">
                              {deviceLabel}
                              {isCurrent && <span className="prf3-badge-current">Current</span>}
                            </p>
                            <p className="prf3-session-meta">{s.ipAddress} · {lastActive}</p>
                          </div>
                        </div>
                      );
                    })}

                    {/* Sentinel for IntersectionObserver */}
                    <div ref={sessBottomRef} style={{ height: 1 }} />

                    {/* Spinner while loading next page */}
                    {sessLoading && sessions.length > 0 && (
                      <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
                        <div className="sess-spinner" />
                      </div>
                    )}

                    {/* End of list message */}
                    {!sessHasMore && sessions.length > 0 && !sessLoading && (
                      <p style={{ fontSize: 11, color: "#cbd5e1", textAlign: "center", padding: "10px 0 2px" }}>
                        All {sessTotal} sessions loaded
                      </p>
                    )}
                  </div>
                </div>


              </div>
            </div>
          )}

        </div>
      </div>

      <ChangePasswordModal
        show={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </main>
  );
};

const Monitor = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

export default ProfileScreen;
